import * as React from "react";

import { Block } from "kanopi-components/Block";
import { Button } from "kanopi-components/Button";
import { Delete } from "baseui/icon";
import { FormInput } from "./FormInput";
import { HeadingXSmall } from "kanopi-components/typography";
import { expandMargin } from "kanopi-components/utils/styles";
import { useFormArray } from "react-weaver";
import { useStyletron } from "kanopi-components/theme/useStyletron";

type TProps = {
  label?: string;
  help?: string;
  value: unknown;
  id?: string;
  onChange: TOnFormEventFunc;
  onError: TOnFormEventFunc;
  inputForm: unknown;
  disabled?: boolean;
  minItems?: number;
  maxItems?: number;
  itemLabel?: string;
};

export const FormListInput: React.FC<TProps> = ({
  value,
  onChange,
  onError,
  inputForm,
  disabled = false,
  minItems = 1,
  maxItems,
  itemLabel = "Item",
}) => {
  const { formsArray, addForm } = useFormArray({
    onChange,
    onError,
    value,
    minItems,
  });
  const allowItemAddition =
    !disabled && (maxItems == null || formsArray.length < maxItems);
  const allowItemRemoval = formsArray.length > minItems;
  return (
    <Block display="flex" flexDirection="column" alignItems="stretch">
      {formsArray.map((formProps, i) => (
        /* eslint-disable react/jsx-key */
        <SubForm
          {...formProps}
          index={i}
          inputForm={inputForm}
          disabled={formProps.disabled || disabled}
          itemLabel={itemLabel}
          allowItemRemoval={allowItemRemoval}
          displayHeading={maxItems == null || maxItems > 1}
        />
        /* eslint-enable react/jsx-key */
      ))}
      {allowItemAddition && (
        <Button
          onClick={addForm}
          kind={Button.KIND.primary}
          size={Button.SIZE.mini}
          shape={Button.SHAPE.pill}
          type="button"
          overrides={{
            BaseButton: {
              style: ({ $theme }) => ({
                display: "flex",
                marginLeft: "auto",
                marginRight: "auto",
                marginTop: $theme.sizing.scale800,
                marginBottom: $theme.sizing.scale1200,
                textTransform: "uppercase",
              }),
            },
          }}
        >
          Add {itemLabel}
        </Button>
      )}
    </Block>
  );
};

type TInnerSubFormProps = {
  removeForm: TOnMouseEventFunc;
  index: number;
  disabled?: boolean;
  itemLabel: string;
  allowItemRemoval: boolean;
  displayHeading?: boolean;
};

function InnerSubForm(props: TInnerSubFormProps) {
  const {
    removeForm,
    index,
    disabled,
    itemLabel,
    allowItemRemoval,
    displayHeading,
    ...restProps
  } = props;
  const [, theme] = useStyletron();
  return (
    <Block className={`item-${index}`}>
      <Block
        marginTop={index > 0 ? "scale1000" : null}
        marginBottom="scale850"
        marginLeft="auto"
        marginRight="auto"
        minWidth={["100%", "100%", "320px"]}
        maxWidth={[null, null, "600px"]}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        {displayHeading ? (
          <HeadingXSmall
            overrides={{
              Block: {
                style: ({ $theme }) => ({
                  ...$theme.typography.HeadingXSmall,
                  textTransform: "uppercase",
                  textAlign: "center",
                  ...expandMargin(0),
                  width: "100%",
                  [`@media screen and (max-width: ${$theme.breakpoints.medium}px)`]: {
                    textAlign: "left",
                  },
                }),
              },
            }}
          >
            {itemLabel} {index + 1}
          </HeadingXSmall>
        ) : null}
        {!disabled && allowItemRemoval && (
          <Button
            type="button"
            onClick={removeForm}
            size={Button.SIZE.mini}
            shape={Button.SHAPE.circle}
            color={theme.colors.contentInverseTertiary}
            aria-label="Remove item"
          >
            <Delete
              title="Remove"
              style={{ minWidth: "24px", fill: theme.colors.contentInversePrimary }}
            />
          </Button>
        )}
      </Block>
      <FormInput prefix={`${index}-`} disabled={disabled} {...restProps} />
    </Block>
  );
}

const SubForm = React.memo(InnerSubForm);
